import { App } from 'antd';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import { ArgsProps, IconType } from 'antd/es/notification/interface';
import { ReactNode, useCallback } from 'react';

export type NotificationFn = (description: ReactNode, options?: ArgsProps) => void;

export interface UseAppNotificationsResult {
	errorNotification: NotificationFn;
	failedNotification: NotificationFn;
	infoNotification: NotificationFn;
	openNotification: (type: IconType, description: ReactNode, options?: ArgsProps) => void;
	successNotification: NotificationFn;
}

export function useAppNotifications(): UseAppNotificationsResult {
	const { notification } = App.useApp();

	const errorNotification = useCallback((description: ReactNode, options?: ArgsProps) => {
		notification.error({
			description,
			message: COPY_GENERIC.ERROR,
			...options
		});
	}, [ notification ]);

	const failedNotification = useCallback((description: ReactNode, options?: ArgsProps) => {
		notification.warning({
			description,
			message: COPY_GENERIC.ERROR,
			...options
		});
	}, [ notification ]);

	const infoNotification = useCallback((description: ReactNode, options?: ArgsProps) => {
		notification.info({
			description,
			message: COPY_GENERIC.INFO,
			...options
		});
	}, [ notification ]);

	const successNotification = useCallback((description: ReactNode, options?: ArgsProps) => {
		notification.success({
			description,
			message: COPY_GENERIC.SUCCESS,
			...options
		});
	}, [ notification ]);

	const openNotification = useCallback((type: IconType, description: ReactNode, options?: ArgsProps) => {
		switch (type) {
		case 'error':
			errorNotification(description, options);
			break;
		case 'info':
			infoNotification(description, options);
			break;
		case 'success':
			successNotification(description, options);
			break;
		case 'warning':
			failedNotification(description, options);
			break;
		default:
			notification.open({
				description,
				message: COPY_GENERIC.ERROR,
				...options
			});
		}
	}, [ errorNotification, failedNotification, infoNotification, notification, successNotification ]);

	return {
		errorNotification,
		failedNotification,
		infoNotification,
		openNotification,
		successNotification
	};
}
